import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { listEvents } from '../services/eventsService';
import { getLayoutPublic } from '../services/layoutsService';
import { getVendor } from '../services/vendorsService';
import { getUserProfile } from '../services/profilesService';
import { listAttendeesByEvent } from '../services/attendeesService';
import { listCapacityByEvent } from '../services/attendeeCapacityService';
import { getFunctionConfig, sfVendorRegistration } from '../services/defaultsService';
import { useAuth } from '../context/AuthContext';
import EmailRecipientsModal from './EmailRecipientsModal';
import { collectUniqueEmails, toCommaDelimitedEmailText } from '../utils/emailRecipients';
import { db } from '../../sdk_initialization';
import './ProfilePage.css';

const BAZAAR_CHECKIN_CACHE_PREFIX = 'bazaarCheckinRows:';
const BAZAAR_CHECKIN_RETURN_UPDATE_KEY = 'bazaarCheckin:returnUpdate';

const isTruthyConsent = (value) => {
  if (value === true || value === 1) return true;
  const normalized = String(value || '').trim().toLowerCase();
  return normalized === 'true' || normalized === '1' || normalized === 'yes' || normalized === 'y';
};

const normalizeCachedTimestamp = (value) => {
  if (!value) return null;
  try {
    if (typeof value?.toDate === 'function') return value.toDate().toISOString();
    if (typeof value?.seconds === 'number') {
      return new Date(value.seconds * 1000 + Math.round((value.nanoseconds || 0) / 1e6)).toISOString();
    }
    if (typeof value === 'string') return value;
    return null;
  } catch {
    return null;
  }
};

// Helper function to limit concurrent promises
const limitConcurrency = async (items, asyncFn, concurrencyLimit = 10) => {
  const results = [];
  for (let i = 0; i < items.length; i += concurrencyLimit) {
    const batch = items.slice(i, i + concurrencyLimit);
    const batchResults = await Promise.all(batch.map(asyncFn));
    results.push(...batchResults);
  }
  return results;
};

const BazaarCheckin = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentUser } = useAuth();

  const initialEventId = searchParams.get('eventId') || '';
  const initialVendorType = searchParams.get('vendorType') || '';
  const initialVendorName = searchParams.get('vendorName') || '';
  const initialContactName = searchParams.get('contactName') || '';
  const initialCheckinDate = searchParams.get('checkinDate') || '';
  const initialBoothNumber = searchParams.get('boothNumber') || '';
  const initialConsentSignedParam = searchParams.get('consentSigned') || '';
  const initialConsentSignedFilter = initialConsentSignedParam === '1'
    ? 'yes'
    : (initialConsentSignedParam === '0' ? 'no' : 'all');
  const initialCheckedInOnly = searchParams.get('checkedIn') === '1';
  const initialCheckedOutOnly = searchParams.get('checkedOut') === '1';
  const initialSortBy = searchParams.get('sortBy') || 'vendor';
  const initialSortDir = searchParams.get('sortDir') || 'asc';

  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState('');
  const [eventChoices, setEventChoices] = useState([]);
  const [eventId, setEventId] = useState(initialEventId);
  const [eventName, setEventName] = useState('');

  const [vendorRows, setVendorRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [lastLoadedAt, setLastLoadedAt] = useState('');

  const [vendorTypeFilter, setVendorTypeFilter] = useState(initialVendorType);
  const [vendorNameFilter, setVendorNameFilter] = useState(initialVendorName);
  const [contactNameFilter, setContactNameFilter] = useState(initialContactName);
  const [checkinDateFilter, setCheckinDateFilter] = useState(initialCheckinDate);
  const [boothNumberFilter, setBoothNumberFilter] = useState(initialBoothNumber);
  const [consentSignedFilter, setConsentSignedFilter] = useState(initialConsentSignedFilter);
  const [checkedInOnly, setCheckedInOnly] = useState(initialCheckedInOnly);
  const [checkedOutOnly, setCheckedOutOnly] = useState(initialCheckedOutOnly);
  const [sortBy, setSortBy] = useState(initialSortBy);
  const [sortDir, setSortDir] = useState(initialSortDir);
  const [selectedVendorIds, setSelectedVendorIds] = useState(new Set());
  const [salesforceBusy, setSalesforceBusy] = useState(false);
  const [salesforceMsg, setSalesforceMsg] = useState('');
  const [attendeeExportBusy, setAttendeeExportBusy] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailModalText, setEmailModalText] = useState('');
  const vendorCacheRef = useRef(new Map());
  const profileCacheRef = useRef(new Map());

  useEffect(() => {
    const next = new URLSearchParams();
    if (eventId) next.set('eventId', eventId);
    if (vendorTypeFilter.trim()) next.set('vendorType', vendorTypeFilter.trim());
    if (vendorNameFilter.trim()) next.set('vendorName', vendorNameFilter.trim());
    if (contactNameFilter.trim()) next.set('contactName', contactNameFilter.trim());
    if (checkinDateFilter.trim()) next.set('checkinDate', checkinDateFilter.trim());
    if (boothNumberFilter.trim()) next.set('boothNumber', boothNumberFilter.trim());
    if (consentSignedFilter === 'yes') next.set('consentSigned', '1');
    if (consentSignedFilter === 'no') next.set('consentSigned', '0');
    if (checkedInOnly) next.set('checkedIn', '1');
    if (checkedOutOnly) next.set('checkedOut', '1');
    if (sortBy) next.set('sortBy', sortBy);
    if (sortDir) next.set('sortDir', sortDir);
    const currentRaw = searchParams.toString();
    const nextRaw = next.toString();
    if (currentRaw !== nextRaw) {
      setSearchParams(next, { replace: true });
    }
  }, [eventId, vendorTypeFilter, vendorNameFilter, contactNameFilter, checkinDateFilter, boothNumberFilter, consentSignedFilter, checkedInOnly, checkedOutOnly, sortBy, sortDir, searchParams, setSearchParams]);

  const detailQueryString = useMemo(() => {
    const params = new URLSearchParams();
    if (eventId) params.set('eventId', eventId);
    if (vendorTypeFilter.trim()) params.set('vendorType', vendorTypeFilter.trim());
    if (vendorNameFilter.trim()) params.set('vendorName', vendorNameFilter.trim());
    if (contactNameFilter.trim()) params.set('contactName', contactNameFilter.trim());
    if (checkinDateFilter.trim()) params.set('checkinDate', checkinDateFilter.trim());
    if (boothNumberFilter.trim()) params.set('boothNumber', boothNumberFilter.trim());
    if (consentSignedFilter === 'yes') params.set('consentSigned', '1');
    if (consentSignedFilter === 'no') params.set('consentSigned', '0');
    if (checkedInOnly) params.set('checkedIn', '1');
    if (checkedOutOnly) params.set('checkedOut', '1');
    if (sortBy) params.set('sortBy', sortBy);
    if (sortDir) params.set('sortDir', sortDir);
    return params.toString();
  }, [eventId, vendorTypeFilter, vendorNameFilter, contactNameFilter, checkinDateFilter, boothNumberFilter, consentSignedFilter, checkedInOnly, checkedOutOnly, sortBy, sortDir]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setEventsLoading(true);
        setEventsError('');
        const all = await listEvents();
        const havingLayout = (all || []).filter(
          ev => ev?.layoutId && (ev?.status || '').toLowerCase() !== 'archived'
        );

        let filtered = havingLayout;
        try {
          if (currentUser?.uid) {
            const prof = await getUserProfile(currentUser.uid);
            const uidLower = String(currentUser.uid || '').toLowerCase();
            if (prof?.userType === 'EventAdmin') {
              filtered = (havingLayout || []).filter(
                ev => Array.isArray(ev.eventAdmins) && ev.eventAdmins.some(a => String(a || '').toLowerCase() === uidLower)
              );
            } else if (prof?.userType === 'Organizer') {
              filtered = (havingLayout || []).filter(
                ev => Array.isArray(ev.organizers) && ev.organizers.some(a => String(a || '').toLowerCase() === uidLower)
              );
            } else if (prof?.userType !== 'Admin') {
              filtered = [];
            }
          }
        } catch (profileErr) {
          filtered = [];
        }

        const entries = [];
        for (const ev of filtered) {
          try {
            const layout = await getLayoutPublic(ev.layoutId);
            if ((layout?.status || '').toLowerCase() !== 'published') continue;
            entries.push({
              eventId: ev.id,
              eventName: ev.name || ev.id,
              salesforceEventId: ev.salesforceEventId || '',
              checkinSchedule: Array.isArray(ev.checkinSchedule) ? ev.checkinSchedule : [],
            });
          } catch {
            // Ignore events with missing/unpublished layouts, matching BoothAdmin behavior.
          }
        }

        entries.sort((a, b) => String(a.eventName).localeCompare(String(b.eventName)));

        if (!cancelled) {
          setEventChoices(entries);
          setEventId(prev => {
            const current = String(prev || '').trim();
            const stillVisible = entries.some(entry => entry.eventId === current);
            return stillVisible ? prev : '';
          });
          setEventName(prev => {
            const selected = entries.find(entry => entry.eventId === eventId);
            if (!selected) return '';
            return selected.eventName || prev || '';
          });
        }
      } catch (e) {
        if (!cancelled) {
          setEventsError('Failed to load events.');
          setEventChoices([]);
          setEventId('');
          setEventName('');
        }
      } finally {
        if (!cancelled) setEventsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentUser?.uid]);

  useEffect(() => {
    const selected = eventChoices.find(choice => choice.eventId === eventId);
    setEventName(selected?.eventName || (eventId || ''));
  }, [eventChoices, eventId]);

  const selectedEventChoice = useMemo(
    () => (eventChoices || []).find((choice) => choice.eventId === eventId) || null,
    [eventChoices, eventId]
  );
  const selectedEventSalesforceId = String(selectedEventChoice?.salesforceEventId || '').trim();
  const missingSalesforceEventId = Boolean(eventId) && !selectedEventSalesforceId;
  const selectedEventCheckinDateOptions = useMemo(() => {
    const schedule = Array.isArray(selectedEventChoice?.checkinSchedule) ? selectedEventChoice.checkinSchedule : [];
    return schedule
      .map((row, index) => ({
        sequence: Number(row?.sequence ?? (index + 1)),
        date: String(row?.date || '').trim(),
      }))
      .filter((row) => row.date)
      .sort((a, b) => a.sequence - b.sequence);
  }, [selectedEventChoice]);

  useEffect(() => {
    if (!checkinDateFilter) return;
    const exists = selectedEventCheckinDateOptions.some((row) => row.date === checkinDateFilter);
    if (!exists) setCheckinDateFilter('');
  }, [selectedEventCheckinDateOptions, checkinDateFilter]);

  const cacheKeyForEvent = useCallback((targetEventId) => {
    return `${BAZAAR_CHECKIN_CACHE_PREFIX}${String(targetEventId || '').trim()}`;
  }, []);

  const readCachedRows = useCallback((targetEventId) => {
    const key = cacheKeyForEvent(targetEventId);
    if (!key || typeof window === 'undefined' || !window.sessionStorage) return null;
    try {
      const raw = window.sessionStorage.getItem(key);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || !Array.isArray(parsed.rows)) return null;
      const normalizedRows = parsed.rows.map((row) => ({
        ...row,
        checkinAt: normalizeCachedTimestamp(row?.checkinAt),
        checkoutAt: normalizeCachedTimestamp(row?.checkoutAt),
      }));
      return { ...parsed, rows: normalizedRows };
    } catch {
      return null;
    }
  }, [cacheKeyForEvent]);

  const writeCachedRows = useCallback((targetEventId, rows) => {
    const key = cacheKeyForEvent(targetEventId);
    if (!key || typeof window === 'undefined' || !window.sessionStorage) return;
    try {
      window.sessionStorage.setItem(key, JSON.stringify({
        rows: Array.isArray(rows) ? rows : [],
        loadedAt: new Date().toISOString(),
      }));
    } catch {
      // Ignore cache write errors (quota/private mode).
    }
  }, [cacheKeyForEvent]);

  const clearCachedRows = useCallback(() => {
    if (typeof window === 'undefined' || !window.sessionStorage) return;
    try {
      const keysToRemove = [];
      for (let index = 0; index < window.sessionStorage.length; index += 1) {
        const key = window.sessionStorage.key(index);
        if (key && key.startsWith(BAZAAR_CHECKIN_CACHE_PREFIX)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => window.sessionStorage.removeItem(key));
      window.sessionStorage.removeItem(BAZAAR_CHECKIN_RETURN_UPDATE_KEY);
    } catch {
      // Ignore cache clear errors.
    }
  }, []);

  const readPendingRowUpdate = useCallback(() => {
    if (typeof window === 'undefined' || !window.sessionStorage) return null;
    try {
      const raw = window.sessionStorage.getItem(BAZAAR_CHECKIN_RETURN_UPDATE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || !parsed.eventId || !parsed.vendorId) return null;
      return parsed;
    } catch {
      return null;
    }
  }, []);

  const clearPendingRowUpdate = useCallback(() => {
    if (typeof window === 'undefined' || !window.sessionStorage) return;
    try {
      window.sessionStorage.removeItem(BAZAAR_CHECKIN_RETURN_UPDATE_KEY);
    } catch {
      // Ignore update clear errors.
    }
  }, []);

  const applyPendingRowUpdate = useCallback((rows, update) => {
    if (!Array.isArray(rows) || !update) return rows;
    const targetVendorId = String(update.vendorId || '').trim();
    if (!targetVendorId) return rows;

    let changed = false;
    const nextRows = rows.map((row) => {
      const rowVendorId = String(row?.vendorId || '').trim();
      if (rowVendorId !== targetVendorId) return row;
      changed = true;
      return {
        ...row,
        checkinAt: update.checkinAt || null,
        checkoutAt: update.checkoutAt || null,
      };
    });

    return changed ? nextRows : rows;
  }, []);

  const loadVendorRows = useCallback(async (targetEventId) => {
    if (!targetEventId) {
      setVendorRows([]);
      setError('');
      setLoading(false);
      setLastLoadedAt('');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const reservationsSnap = await getDocs(
        query(
          collection(db, 'boothReservations'),
          where('eventId', '==', targetEventId),
          where('status', '==', 'Reserved')
        )
      );
      const reservations = reservationsSnap.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
      const vendorMap = new Map();

      reservations.forEach((reservation) => {
        const rowVendorId = String(reservation?.vendorId || '').trim();
        if (!rowVendorId) return;

        if (!vendorMap.has(rowVendorId)) {
          vendorMap.set(rowVendorId, {
            vendorId: rowVendorId,
            boothNumbers: new Set(),
          });
        }

        const entry = vendorMap.get(rowVendorId);
        const boothNumber = String(
          reservation?.boothNumber || reservation?.number || reservation?.boothId || ''
        ).trim();
        if (boothNumber) {
          entry.boothNumbers.add(boothNumber);
        }
      });

      const attendees = await listAttendeesByEvent(targetEventId);
      const attendeeCounts = new Map();
      (attendees || []).forEach(attendee => {
        const rowVendorId = String(attendee?.vendorId || '').trim();
        if (!rowVendorId) return;
        attendeeCounts.set(rowVendorId, (attendeeCounts.get(rowVendorId) || 0) + 1);
      });

      const capacityRecords = await listCapacityByEvent(targetEventId).catch(() => []);
      const purchasedByVendor = new Map();
      (capacityRecords || []).forEach((capacity) => {
        const capacityVendorId = String(capacity?.vendorId || '').trim();
        if (!capacityVendorId) return;
        purchasedByVendor.set(capacityVendorId, Number(capacity?.purchasedAdditionalAttendees || 0));
      });

      const checkinsSnap = await getDocs(
        query(collection(db, 'checkins'), where('eventId', '==', targetEventId))
      );
      const checkinsByVendor = new Map();
      checkinsSnap.forEach((docSnap) => {
        const data = docSnap.data() || {};
        const rowVendorId = String(data.vendorId || '').trim();
        if (!rowVendorId) return;
        checkinsByVendor.set(rowVendorId, data);
      });

      const precheckinsSnap = await getDocs(
        query(collection(db, 'precheckins'), where('eventId', '==', targetEventId))
      );
      const precheckinByVendor = new Map();
      precheckinsSnap.forEach((docSnap) => {
        const data = docSnap.data() || {};
        const rowVendorId = String(data.vendorId || '').trim();
        if (!rowVendorId) return;
        const existing = precheckinByVendor.get(rowVendorId);
        if (!existing) {
          precheckinByVendor.set(rowVendorId, data);
          return;
        }
        const existingMs = existing?.updatedAt?.toMillis ? existing.updatedAt.toMillis() : 0;
        const nextMs = data?.updatedAt?.toMillis ? data.updatedAt.toMillis() : 0;
        if (nextMs >= existingMs) {
          precheckinByVendor.set(rowVendorId, data);
        }
      });

      const vendorEntries = Array.from(vendorMap.values());
      const vendorCache = vendorCacheRef.current;
      const profileCache = profileCacheRef.current;

      const missingVendorIds = vendorEntries
        .map((entry) => String(entry?.vendorId || '').trim())
        .filter((vendorIdValue) => vendorIdValue && !vendorCache.has(vendorIdValue));

      if (missingVendorIds.length > 0) {
        await Promise.all(
          missingVendorIds.map(async (vendorIdValue) => {
            const vendor = await getVendor(vendorIdValue).catch(() => null);
            vendorCache.set(vendorIdValue, vendor || null);
          })
        );
      }

      const contactUidByVendorId = new Map();
      const missingContactUids = new Set();
      vendorEntries.forEach((entry) => {
        const vendor = vendorCache.get(entry.vendorId) || null;
        const vendorAdminUid = String(vendor?.vendorAdmin || '').trim();
        const memberUids = Array.isArray(vendor?.memberUids) ? vendor.memberUids : [];
        const fallbackMemberUid = memberUids.find((uid) => String(uid || '').trim()) || '';
        const contactUid = vendorAdminUid || String(fallbackMemberUid || '').trim();
        if (!contactUid) return;
        contactUidByVendorId.set(entry.vendorId, contactUid);
        if (!profileCache.has(contactUid)) missingContactUids.add(contactUid);
      });

      if (missingContactUids.size > 0) {
        await Promise.all(
          Array.from(missingContactUids).map(async (contactUid) => {
            const profile = await getUserProfile(contactUid).catch(() => null);
            profileCache.set(contactUid, profile || null);
          })
        );
      }

      const rows = vendorEntries.map((entry) => {
        const vendor = vendorCache.get(entry.vendorId) || null;
        const contactUid = contactUidByVendorId.get(entry.vendorId) || '';
        const contactProfile = contactUid ? (profileCache.get(contactUid) || null) : null;

        const contactName =
          `${contactProfile?.firstName || ''} ${contactProfile?.lastName || ''}`.trim() ||
          contactProfile?.name ||
          contactProfile?.email ||
          '';
        const contactEmail = String(contactProfile?.email || '').trim();

        const checkin = checkinsByVendor.get(entry.vendorId) || null;
        const precheckin = precheckinByVendor.get(entry.vendorId) || null;
        const isCheckedIn = checkin?.checkedIn === true || (checkin?.checkedIn == null && Boolean(checkin?.checkinAt));
        const isCheckedOut = checkin?.checkedOut === true || (checkin?.checkedOut == null && Boolean(checkin?.checkoutAt));
        const selectedCheckinDate = String(
          precheckin?.selectedCheckinScheduleDate || precheckin?.selectedCheckinSchedule?.date || ''
        ).trim();
        const consentSigned =
          isTruthyConsent(precheckin?.consentSigned)
          || isTruthyConsent(precheckin?.consentAccepted)
          || isTruthyConsent(precheckin?.consent?.signed)
          || isTruthyConsent(precheckin?.consent?.agreed)
          || isTruthyConsent(precheckin?.consent?.acknowledged);

        return {
          vendorId: entry.vendorId,
          vendorNumber: vendor?.vendorNumber || '',
          vendorName: vendor?.name || entry.vendorId,
          vendorEmail: String(vendor?.email || '').trim(),
          vendorType: vendor?.type || '',
          contactName,
          contactEmail,
          boothCount: entry.boothNumbers.size,
          attendeeCount: attendeeCounts.get(entry.vendorId) || 0,
          purchasedAttendees: purchasedByVendor.has(entry.vendorId)
            ? purchasedByVendor.get(entry.vendorId)
            : null,
          checkinAt: isCheckedIn ? (checkin?.checkinAt || null) : null,
          checkoutAt: isCheckedOut ? (checkin?.checkoutAt || null) : null,
          selectedCheckinDate,
          consentSigned,
          boothNumbers: Array.from(entry.boothNumbers).sort((a, b) =>
            a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
          ),
        };
      });

      rows.sort((a, b) =>
        String(a.vendorName || '').localeCompare(String(b.vendorName || ''), undefined, { sensitivity: 'base' })
      );

      setVendorRows(rows);
      setLoading(false);
      const loadedAtIso = new Date().toISOString();
      setLastLoadedAt(loadedAtIso);
      writeCachedRows(targetEventId, rows);
    } catch (e) {
      setError(e?.message || 'Failed to load vendor check-in data');
      setVendorRows([]);
      setLoading(false);
    }
  }, [writeCachedRows]);

  useEffect(() => {
    if (!eventId) {
      setVendorRows([]);
      setLoading(false);
      setError('');
      setLastLoadedAt('');
      return;
    }

    const cached = readCachedRows(eventId);
    if (cached) {
      const pendingUpdate = readPendingRowUpdate();
      const shouldApplyPending = String(pendingUpdate?.eventId || '').trim() === String(eventId || '').trim();
      const nextRows = shouldApplyPending
        ? applyPendingRowUpdate(Array.isArray(cached.rows) ? cached.rows : [], pendingUpdate)
        : (Array.isArray(cached.rows) ? cached.rows : []);
      const loadedAtIso = shouldApplyPending && nextRows !== cached.rows
        ? new Date().toISOString()
        : String(cached.loadedAt || '');

      setVendorRows(nextRows);
      setError('');
      setLoading(false);
      setLastLoadedAt(loadedAtIso);

      if (shouldApplyPending && nextRows !== cached.rows) {
        writeCachedRows(eventId, nextRows);
        clearPendingRowUpdate();
      }
      return;
    }

    loadVendorRows(eventId);
  }, [eventId, readCachedRows, readPendingRowUpdate, applyPendingRowUpdate, loadVendorRows, writeCachedRows, clearPendingRowUpdate]);

  useEffect(() => {
    if (!eventId || loading || !Array.isArray(vendorRows) || vendorRows.length === 0) return;

    const pendingUpdate = readPendingRowUpdate();
    if (String(pendingUpdate?.eventId || '').trim() !== String(eventId || '').trim()) return;

    const nextRows = applyPendingRowUpdate(vendorRows, pendingUpdate);
    if (nextRows === vendorRows) return;

    setVendorRows(nextRows);
    setLastLoadedAt(new Date().toISOString());
    writeCachedRows(eventId, nextRows);
    clearPendingRowUpdate();
  }, [eventId, loading, vendorRows, readPendingRowUpdate, applyPendingRowUpdate, writeCachedRows, clearPendingRowUpdate]);

  const handleRefresh = useCallback(() => {
    if (!eventId) return;
    loadVendorRows(eventId);
  }, [eventId, loadVendorRows]);

  const handleExitPage = useCallback((target) => {
    clearCachedRows();
    navigate(target);
  }, [clearCachedRows, navigate]);

  const handleBack = useCallback(() => {
    clearCachedRows();
    navigate(-1);
  }, [clearCachedRows, navigate]);

  const handleOpenVendorDetail = useCallback((vendorIdToOpen) => {
    const nextVendorId = String(vendorIdToOpen || '').trim();
    if (!nextVendorId) return;
    navigate(`/admin/bazaar-checkin/${nextVendorId}${detailQueryString ? `?${detailQueryString}` : ''}`);
  }, [navigate, detailQueryString]);

  const filtered = useMemo(() => {
    const vendorNameQuery = vendorNameFilter.trim().toLowerCase();
    const contactNameQuery = contactNameFilter.trim().toLowerCase();
    const vendorTypeQuery = vendorTypeFilter.trim().toLowerCase();
    const boothNumberQuery = boothNumberFilter.trim().toLowerCase();

    return vendorRows.filter(row => {
      if (vendorNameQuery && !String(row.vendorName || '').toLowerCase().includes(vendorNameQuery)) return false;
      if (contactNameQuery && !String(row.contactName || '').toLowerCase().includes(contactNameQuery)) return false;
      if (vendorTypeQuery && !String(row.vendorType || '').toLowerCase().includes(vendorTypeQuery)) return false;
      if (checkinDateFilter && String(row.selectedCheckinDate || '').trim() !== checkinDateFilter) return false;
      if (boothNumberQuery) {
        const boothText = Array.isArray(row.boothNumbers)
          ? row.boothNumbers.map((booth) => String(booth || '').trim()).join(' ').toLowerCase()
          : String(row.boothNumbers || '').toLowerCase();
        if (!boothText.includes(boothNumberQuery)) return false;
      }
      if (consentSignedFilter === 'yes' && row.consentSigned !== true) return false;
      if (consentSignedFilter === 'no' && row.consentSigned === true) return false;
      if (checkedInOnly && !row.checkinAt) return false;
      if (checkedOutOnly && !row.checkoutAt) return false;
      return true;
    });
  }, [vendorRows, vendorNameFilter, contactNameFilter, vendorTypeFilter, checkinDateFilter, boothNumberFilter, consentSignedFilter, checkedInOnly, checkedOutOnly]);

  const summaryFiltered = useMemo(
    () => filtered.reduce(
      (acc, row) => {
        acc.vendors += 1;
        acc.booths += Number(row.boothCount || 0);
        acc.attendees += Number(row.attendeeCount || 0);
        if (row.consentSigned === true) acc.signed += 1;
        if (row.checkinAt) acc.checkedIn += 1;
        if (row.checkoutAt) acc.checkedOut += 1;
        return acc;
      },
      { vendors: 0, booths: 0, attendees: 0, signed: 0, checkedIn: 0, checkedOut: 0 }
    ),
    [filtered]
  );

  const vendorCountByDate = useMemo(() => {
    const counts = new Map();
    filtered.forEach((row) => {
      const date = String(row.selectedCheckinDate || '').trim();
      if (!date) return;
      counts.set(date, (counts.get(date) || 0) + 1);
    });
    return counts;
  }, [filtered]);

  const dateCountSummary = useMemo(() => {
    const orderedDates = selectedEventCheckinDateOptions
      .map((row) => row.date)
      .filter((date) => vendorCountByDate.has(date));
    // Also include any dates in filtered rows that aren't in the schedule (legacy)
    vendorCountByDate.forEach((_, date) => {
      if (!orderedDates.includes(date)) orderedDates.push(date);
    });
    return orderedDates.map((date) => `${date}: ${vendorCountByDate.get(date) || 0}`);
  }, [selectedEventCheckinDateOptions, vendorCountByDate]);

  useEffect(() => {
    setSelectedVendorIds((prev) => {
      const currentIds = new Set((vendorRows || []).map((row) => String(row.vendorId || '').trim()).filter(Boolean));
      const next = new Set();
      prev.forEach((id) => {
        if (currentIds.has(id)) next.add(id);
      });
      return next;
    });
  }, [vendorRows]);

  const filteredVendorIds = useMemo(
    () => new Set((filtered || []).map((row) => String(row.vendorId || '').trim()).filter(Boolean)),
    [filtered]
  );

  const selectedCount = useMemo(
    () => Array.from(selectedVendorIds).filter((id) => filteredVendorIds.has(id)).length,
    [selectedVendorIds, filteredVendorIds]
  );

  const allFilteredSelected = useMemo(
    () => filteredVendorIds.size > 0 && Array.from(filteredVendorIds).every((id) => selectedVendorIds.has(id)),
    [filteredVendorIds, selectedVendorIds]
  );

  const toggleRowSelection = useCallback((vendorId) => {
    const id = String(vendorId || '').trim();
    if (!id) return;
    setSelectedVendorIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAllFiltered = useCallback((nextChecked) => {
    setSelectedVendorIds((prev) => {
      const next = new Set(prev);
      if (nextChecked) {
        filteredVendorIds.forEach((id) => next.add(id));
      } else {
        filteredVendorIds.forEach((id) => next.delete(id));
      }
      return next;
    });
  }, [filteredVendorIds]);

  const handleAttendeeDownload = useCallback(async () => {
    if (!eventId) return;

    const selectedIdsInFiltered = Array.from(selectedVendorIds)
      .map((id) => String(id || '').trim())
      .filter((id) => filteredVendorIds.has(id));
    const targetVendorIds = selectedIdsInFiltered.length > 0
      ? new Set(selectedIdsInFiltered)
      : new Set(filteredVendorIds);

    if (targetVendorIds.size === 0) {
      window.alert('No rows available to export. Apply filters or select one or more rows.');
      return;
    }

    const vendorRowsMap = new Map(
      (vendorRows || []).map((row) => [String(row.vendorId || '').trim(), row])
    );

    try {
      setAttendeeExportBusy(true);
      const attendees = await listAttendeesByEvent(eventId);

      const scopedAttendees = (attendees || []).filter((attendee) => {
        const rowVendorId = String(attendee?.vendorId || '').trim();
        return targetVendorIds.has(rowVendorId);
      });

      const escapeCell = (val) => {
        const str = String(val == null ? '' : val);
        if (str.includes(',') || str.includes('"') || str.includes('\n')) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      };

      const headers = [
        'Event ID',
        'Vendor #',
        'Vendor Name',
        'Contact Name',
        'Attendee ID',
        'Is Primary',
        'Delete Flag',
        'First Name',
        'Last Name',
        'Email',
        'Gender',
        'Year of Birth',
      ];

      const rows = scopedAttendees.map((attendee) => {
        const rowVendorId = String(attendee?.vendorId || '').trim();
        const vendorRow = vendorRowsMap.get(rowVendorId) || {};
        return [
          escapeCell(eventId),
          escapeCell(vendorRow.vendorNumber || ''),
          escapeCell(vendorRow.vendorName || ''),
          escapeCell(vendorRow.contactName || ''),
          escapeCell(attendee?.id || ''),
          escapeCell(attendee?.isPrimary === true ? 'true' : 'false'),
          escapeCell(attendee?.delete === true ? 'true' : 'false'),
          escapeCell(attendee?.firstName || ''),
          escapeCell(attendee?.lastName || ''),
          escapeCell(attendee?.email || ''),
          escapeCell(attendee?.gender || ''),
          escapeCell(attendee?.yob || ''),
        ].join(',');
      });

      const csv = [headers.map(escapeCell).join(','), ...rows].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `attendees-${eventId}-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      window.alert(`Failed to load attendee data: ${e?.message || 'Unknown error'}`);
    } finally {
      setAttendeeExportBusy(false);
    }
  }, [eventId, selectedVendorIds, filteredVendorIds, vendorRows]);

  const handleOpenSelectedEmailsModal = useCallback(() => {
    const targetRows = (filtered || []).filter((row) => {
      const id = String(row?.vendorId || '').trim();
      return id && selectedVendorIds.has(id);
    });
    const emails = collectUniqueEmails(targetRows, [
      (row) => row.vendorEmail,
      (row) => row.contactEmail,
    ]);
    setEmailModalText(toCommaDelimitedEmailText(emails));
    setShowEmailModal(true);
  }, [filtered, selectedVendorIds]);

  const handleMassSalesforceUpdate = useCallback(async () => {
    if (!eventId) return;

    if (missingSalesforceEventId) {
      window.alert('This event is missing Salesforce Event ID (salesforceEventId). Please set it in Events before running Update Salesforce.');
      return;
    }

    const targetIds = Array.from(selectedVendorIds).filter((id) => filteredVendorIds.has(id));
    if (targetIds.length === 0) {
      setSalesforceMsg('Select one or more rows before running Salesforce update.');
      return;
    }

    setSalesforceBusy(true);
    setSalesforceMsg('');

    try {
      const appSettings = await getFunctionConfig();
      const outcomes = await limitConcurrency(
        targetIds,
        async (vendorId) => {
          try {
            await sfVendorRegistration(appSettings || {}, eventId, vendorId);
            return { ok: true, vendorId };
          } catch (err) {
            return { ok: false, vendorId, message: err?.message || 'Unknown error' };
          }
        },
        10 // Max 10 concurrent threads
      );

      const successCount = outcomes.filter((x) => x.ok).length;
      const failed = outcomes.filter((x) => !x.ok);
      if (failed.length > 0) {
        const firstErr = failed[0]?.message || 'Unknown error';
        setSalesforceMsg(`Salesforce update complete: ${successCount} succeeded, ${failed.length} failed. First error: ${firstErr}`);
        window.alert(`Salesforce mass update completed.\nPushed: ${successCount}\nFailed: ${failed.length}\nTotal selected: ${targetIds.length}\n\nFirst error: ${firstErr}`);
      } else {
        setSalesforceMsg(`Salesforce update complete: ${successCount} succeeded.`);
        window.alert(`Salesforce mass update completed.\nPushed: ${successCount}\nFailed: 0\nTotal selected: ${targetIds.length}`);
      }
    } catch (err) {
      setSalesforceMsg(`Salesforce update failed: ${err?.message || 'Unknown error'}`);
      window.alert(`Salesforce mass update failed.\n${err?.message || 'Unknown error'}`);
    } finally {
      setSalesforceBusy(false);
    }
  }, [eventId, missingSalesforceEventId, selectedVendorIds, filteredVendorIds]);

  const busyMode = useMemo(() => {
    if (loading) return 'loading';
    if (salesforceBusy) return 'salesforce';
    if (isExporting) return 'vendorExport';
    if (attendeeExportBusy) return 'attendeeExport';
    return '';
  }, [loading, salesforceBusy, isExporting, attendeeExportBusy]);

  const busyLabel = useMemo(() => {
    if (busyMode === 'loading') return 'Loading event data...';
    if (busyMode === 'salesforce') return 'Updating Salesforce...';
    if (busyMode === 'vendorExport') return 'Preparing vendor download...';
    if (busyMode === 'attendeeExport') return 'Preparing attendee download...';
    return '';
  }, [busyMode]);

  const sortedFiltered = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1;
    const copy = Array.isArray(filtered) ? filtered.slice() : [];
    copy.sort((a, b) => {
      if (sortBy === 'vendor') {
        const aa = String(a.vendorName || '').toLowerCase();
        const bb = String(b.vendorName || '').toLowerCase();
        return aa === bb ? 0 : (aa > bb ? dir : -dir);
      }
      if (sortBy === 'type') {
        const aa = String(a.vendorType || '').toLowerCase();
        const bb = String(b.vendorType || '').toLowerCase();
        return aa === bb ? 0 : (aa > bb ? dir : -dir);
      }
      if (sortBy === 'contact') {
        const aa = String(a.contactName || '').toLowerCase();
        const bb = String(b.contactName || '').toLowerCase();
        return aa === bb ? 0 : (aa > bb ? dir : -dir);
      }
      if (sortBy === 'booths') {
        return (Number(a.boothCount || 0) - Number(b.boothCount || 0)) * dir;
      }
      if (sortBy === 'attendees') {
        return (Number(a.attendeeCount || 0) - Number(b.attendeeCount || 0)) * dir;
      }
      if (sortBy === 'checkindate') {
        return String(a.selectedCheckinDate || '').localeCompare(String(b.selectedCheckinDate || '')) * dir;
      }
      if (sortBy === 'signed') {
        const aa = a.consentSigned === true ? 1 : 0;
        const bb = b.consentSigned === true ? 1 : 0;
        return (aa - bb) * dir;
      }
      if (sortBy === 'checkin') {
        const aa = a.checkinAt ? (typeof a.checkinAt.toMillis === 'function' ? a.checkinAt.toMillis() : new Date(a.checkinAt).getTime()) : 0;
        const bb = b.checkinAt ? (typeof b.checkinAt.toMillis === 'function' ? b.checkinAt.toMillis() : new Date(b.checkinAt).getTime()) : 0;
        return (aa - bb) * dir;
      }
      if (sortBy === 'checkout') {
        const aa = a.checkoutAt ? (typeof a.checkoutAt.toMillis === 'function' ? a.checkoutAt.toMillis() : new Date(a.checkoutAt).getTime()) : 0;
        const bb = b.checkoutAt ? (typeof b.checkoutAt.toMillis === 'function' ? b.checkoutAt.toMillis() : new Date(b.checkoutAt).getTime()) : 0;
        return (aa - bb) * dir;
      }
      return 0;
    });
    return copy;
  }, [filtered, sortBy, sortDir]);

  const fmtDateTime = useCallback((value) => {
    if (!value) return '';
    try {
      const d = typeof value?.toDate === 'function' ? value.toDate() : new Date(value);
      if (isNaN(d.getTime())) return '';
      return d.toLocaleString();
    } catch {
      return '';
    }
  }, []);

  const handleExportVendorData = useCallback(async () => {
    if (!eventId) {
      window.alert('Please select an event first.');
      return;
    }
    if (vendorRows.length === 0) {
      window.alert('No vendor data loaded. Please load data first.');
      return;
    }
    const label = eventName || eventId;
    const confirmed = window.confirm(`Download Vendor Data for ${label}?`);
    if (!confirmed) return;

    try {
      setIsExporting(true);

      const normalizeText = (value) => {
        if (value == null) return '';
        const text = String(value).trim();
        if (!text) return '';
        const lower = text.toLowerCase();
        if (lower === 'null' || lower === 'undefined') return '';
        return text;
      };

      const csvEscape = (value) => {
        const raw = value == null ? '' : String(value);
        if (/[",\n]/.test(raw)) return `"${raw.replace(/"/g, '""')}"`;
        return raw;
      };

      const fmtTs = (value) => {
        if (!value) return '';
        try {
          const d = typeof value?.toDate === 'function' ? value.toDate()
            : (typeof value?.seconds === 'number' ? new Date(value.seconds * 1000) : new Date(value));
          return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
        } catch { return ''; }
      };

      const formatAddress = (v) => {
        if (!v || typeof v !== 'object') return '';
        return [v.addressStreet, v.addressCity, v.addressState, v.addressZip, v.addressCountry]
          .map(p => normalizeText(p)).filter(Boolean).join(', ');
      };

      // Fetch full vendor docs and precheckins in parallel
      const vendorIds = vendorRows.map(r => r.vendorId).filter(Boolean);
      const [vendorDocs, precheckinsSnap] = await Promise.all([
        Promise.all(vendorIds.map(vid => getVendor(vid).catch(() => null))),
        getDocs(query(collection(db, 'precheckins'), where('eventId', '==', eventId))).catch(() => null),
      ]);

      const vendorDocMap = new Map();
      vendorDocs.forEach((v, i) => { if (v) vendorDocMap.set(vendorIds[i], v); });

      // Fetch vendor admin profiles
      const profileUids = new Set();
      vendorDocMap.forEach(v => {
        const uid = String(v.vendorAdmin || '').trim();
        if (uid) profileUids.add(uid);
      });
      const profileArr = await Promise.all(
        Array.from(profileUids).map(uid => getUserProfile(uid).catch(() => null))
      );
      const profileDocMap = new Map();
      Array.from(profileUids).forEach((uid, i) => { if (profileArr[i]) profileDocMap.set(uid, profileArr[i]); });

      // Build full precheckin map (latest by updatedAt per vendor)
      const precheckinByVendor = new Map();
      (precheckinsSnap?.docs || []).forEach((d) => {
        const data = d.data() || {};
        const vid = String(data.vendorId || '').trim();
        if (!vid) return;
        const existing = precheckinByVendor.get(vid);
        if (!existing) { precheckinByVendor.set(vid, data); return; }
        const eMs = existing?.updatedAt?.toMillis ? existing.updatedAt.toMillis() : 0;
        const nMs = data?.updatedAt?.toMillis ? data.updatedAt.toMillis() : 0;
        if (nMs >= eMs) precheckinByVendor.set(vid, data);
      });

      const columns = [
        'Vendor #', 'Vendor Name', 'Vendor Type', 'Vendor Address', 'Vendor Email', 'Vendor Phone',
        'Contact Name', 'Contact Email', 'Contact Phone',
        'Booth Count', 'Booth #s', 'Attendee Count',
        'Consent Signed By', 'Consent Signed Date', 'Check-in Date', 'Required Items By Booth',
        'Checked In', 'Checked Out',
      ];

      const parseBoothSortNumber = (value) => {
        const text = String(value || '').trim();
        const match = text.match(/\d+/);
        if (!match) return Number.POSITIVE_INFINITY;
        const n = Number(match[0]);
        return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY;
      };

      const parseRequiredItemSelections = (pre) => {
        const rows = Array.isArray(pre?.requiredItemSelections)
          ? pre.requiredItemSelections
          : (Array.isArray(pre?.requiredItemsSelections) ? pre.requiredItemsSelections : []);

        return rows
          .map((r) => ({
            name: String(r?.name || r?.itemName || '').trim(),
            boothNumber: String(r?.boothNumber || r?.booth || '').trim(),
          }))
          .filter((r) => r.name);
      };

      const formatRequiredItemsByBooth = (entries) => {
        if (!Array.isArray(entries) || entries.length === 0) return '';

        const grouped = new Map();
        entries.forEach((entry) => {
          const boothNumber = String(entry.boothNumber || '').trim() || 'Unspecified booth';
          if (!grouped.has(boothNumber)) grouped.set(boothNumber, []);
          grouped.get(boothNumber).push(String(entry.name || '').trim());
        });

        return Array.from(grouped.entries())
          .sort((a, b) => {
            const an = parseBoothSortNumber(a[0]);
            const bn = parseBoothSortNumber(b[0]);
            if (an !== bn) return an - bn;
            return String(a[0] || '').localeCompare(String(b[0] || ''), undefined, { numeric: true, sensitivity: 'base' });
          })
          .map(([boothNumber, itemNames]) => `Booth ${boothNumber}: ${itemNames.join('; ')}`)
          .join(' | ');
      };

      const requiredItemDetailColumns = [
        'Vendor #', 'Vendor Name', 'Booth #', 'Required Item',
      ];
      const requiredItemDetailRows = [];

      const itemSummaryMap = new Map(); // itemLower -> { itemName, totalCount, booths:Set<string> }
      const itemBoothCountMap = new Map(); // itemLower::boothLower -> { itemName, boothNumber, count }

      const dataRows = vendorRows.map(row => {
        const v = vendorDocMap.get(row.vendorId) || {};
        const contactUid = String(v.vendorAdmin || '').trim();
        const p = contactUid ? (profileDocMap.get(contactUid) || {}) : {};
        const pre = precheckinByVendor.get(row.vendorId) || {};

        const vendorNumber = normalizeText(v.vendorNumber);
        const vendorAddress = normalizeText(formatAddress(v));
        const vendorEmail = normalizeText(v.email);
        const vendorPhone = normalizeText(
          [String(v.businessAreaCode || v.countryCode || '').trim(), String(v.phoneNumber || v.phone || '').trim()]
            .filter(Boolean).join(' ')
        );
        const contactEmail = normalizeText(p.email);
        const contactPhone = normalizeText(p.phone || p.phoneNumber || '');

        const consentSigned = isTruthyConsent(pre.consentSigned) || isTruthyConsent(pre.consentAccepted)
          || isTruthyConsent(pre.consent?.signed) || isTruthyConsent(pre.consent?.agreed);
        const consentBy = String(pre.consentFullName || pre.consent?.fullName || pre.consent?.printFullName || '').trim();
        const consentDate = String(pre.consentDate || pre.consent?.signedDate || '').trim();
        const requiredItemEntries = parseRequiredItemSelections(pre);
        const requiredItemsByBooth = formatRequiredItemsByBooth(requiredItemEntries);

        requiredItemEntries.forEach((entry) => {
          const itemName = String(entry.name || '').trim();
          const boothNumber = String(entry.boothNumber || '').trim() || 'Unspecified booth';
          if (!itemName) return;

          requiredItemDetailRows.push([
            vendorNumber,
            normalizeText(row.vendorName),
            boothNumber,
            itemName,
          ]);

          const itemKey = itemName.toLowerCase();
          if (!itemSummaryMap.has(itemKey)) {
            itemSummaryMap.set(itemKey, { itemName, totalCount: 0, booths: new Set() });
          }
          const summary = itemSummaryMap.get(itemKey);
          summary.totalCount += 1;
          summary.booths.add(boothNumber);

          const itemBoothKey = `${itemKey}::${boothNumber.toLowerCase()}`;
          if (!itemBoothCountMap.has(itemBoothKey)) {
            itemBoothCountMap.set(itemBoothKey, { itemName, boothNumber, count: 0 });
          }
          itemBoothCountMap.get(itemBoothKey).count += 1;
        });

        return [
          vendorNumber,
          normalizeText(row.vendorName),
          normalizeText(row.vendorType),
          vendorAddress,
          vendorEmail,
          vendorPhone,
          normalizeText(row.contactName),
          contactEmail,
          contactPhone,
          row.boothCount || 0,
          (row.boothNumbers || []).join(', '),
          row.attendeeCount || 0,
          consentSigned ? (consentBy || 'Yes') : '',
          consentDate,
          row.selectedCheckinDate || '',
          requiredItemsByBooth,
          row.checkinAt ? (fmtTs(row.checkinAt) || 'Yes') : 'No',
          row.checkoutAt ? (fmtTs(row.checkoutAt) || 'Yes') : 'No',
        ];
      }).sort((a, b) => String(a[1] || '').localeCompare(String(b[1] || ''), undefined, { sensitivity: 'base' }));

      requiredItemDetailRows.sort((a, b) => {
        const itemCmp = String(a[3] || '').localeCompare(String(b[3] || ''), undefined, { sensitivity: 'base' });
        if (itemCmp !== 0) return itemCmp;
        const an = parseBoothSortNumber(a[2]);
        const bn = parseBoothSortNumber(b[2]);
        if (an !== bn) return an - bn;
        const boothCmp = String(a[2] || '').localeCompare(String(b[2] || ''), undefined, { numeric: true, sensitivity: 'base' });
        if (boothCmp !== 0) return boothCmp;
        return String(a[1] || '').localeCompare(String(b[1] || ''), undefined, { sensitivity: 'base' });
      });

      const requiredItemSummaryColumns = ['Required Item', 'Total Needed', 'Booth Count', 'Booth #s'];
      const requiredItemSummaryRows = Array.from(itemSummaryMap.values())
        .map((entry) => {
          const boothList = Array.from(entry.booths)
            .sort((a, b) => {
              const an = parseBoothSortNumber(a);
              const bn = parseBoothSortNumber(b);
              if (an !== bn) return an - bn;
              return String(a || '').localeCompare(String(b || ''), undefined, { numeric: true, sensitivity: 'base' });
            });
          return [entry.itemName, entry.totalCount, boothList.length, boothList.join(', ')];
        })
        .sort((a, b) => String(a[0] || '').localeCompare(String(b[0] || ''), undefined, { sensitivity: 'base' }));

      const requiredItemByBoothColumns = ['Required Item', 'Booth #', 'Needed Count'];
      const requiredItemByBoothRows = Array.from(itemBoothCountMap.values())
        .map((entry) => [entry.itemName, entry.boothNumber, entry.count])
        .sort((a, b) => {
          const itemCmp = String(a[0] || '').localeCompare(String(b[0] || ''), undefined, { sensitivity: 'base' });
          if (itemCmp !== 0) return itemCmp;
          const an = parseBoothSortNumber(a[1]);
          const bn = parseBoothSortNumber(b[1]);
          if (an !== bn) return an - bn;
          return String(a[1] || '').localeCompare(String(b[1] || ''), undefined, { numeric: true, sensitivity: 'base' });
        });

      const timePrinted = new Intl.DateTimeFormat('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: '2-digit', minute: '2-digit', hour12: false,
      }).format(new Date());

      const summaryLine = [
        'Event Name', label,
        'Total Vendors', dataRows.length,
        'Total Booths', vendorRows.reduce((s, r) => s + (r.boothCount || 0), 0),
        'Time Printed', timePrinted,
      ];

      const lines = [
        csvEscape('Event Vendors Report'),
        summaryLine.map(csvEscape).join(','),
        columns.map(csvEscape).join(','),
        ...dataRows.map(r => r.map(csvEscape).join(',')),
        '',
        csvEscape('Required Item Selection Details (One row per booth-item selection)'),
        requiredItemDetailColumns.map(csvEscape).join(','),
        ...(requiredItemDetailRows.length > 0
          ? requiredItemDetailRows.map(r => r.map(csvEscape).join(','))
          : [csvEscape('No required item selections found for this export.')]),
        '',
        csvEscape('Required Item Summary (Counts by item type)'),
        requiredItemSummaryColumns.map(csvEscape).join(','),
        ...(requiredItemSummaryRows.length > 0
          ? requiredItemSummaryRows.map(r => r.map(csvEscape).join(','))
          : [csvEscape('No required item selections found for this export.')]),
        '',
        csvEscape('Required Item Counts by Booth'),
        requiredItemByBoothColumns.map(csvEscape).join(','),
        ...(requiredItemByBoothRows.length > 0
          ? requiredItemByBoothRows.map(r => r.map(csvEscape).join(','))
          : [csvEscape('No required item selections found for this export.')]),
      ];
      const csv = `${lines.join('\n')}\n`;

      const safeEvent = String(label || 'event').replace(/[^a-z0-9_-]/gi, '_');
      const dateStr = new Date().toISOString().slice(0, 10);
      const filename = `vendor_data_${safeEvent}_${dateStr}.csv`;

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(url);

      window.alert(`Export complete. ${dataRows.length} vendor record(s) downloaded.`);
    } catch (e) {
      console.error('Failed to export vendor data:', e);
      window.alert('Failed to export data. Please try again.');
    } finally {
      setIsExporting(false);
    }
  }, [eventId, eventName, vendorRows]);

  return (
    <div className="profile-container profile-wide-container">
      {busyMode && (
        <div
          aria-hidden="true"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1500,
            cursor: 'wait',
            background: 'rgba(15, 23, 42, 0.22)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
            <div
              style={{
                width: 160,
                height: 160,
                borderRadius: '50%',
                background: 'rgba(0,0,0,0.55)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 28px rgba(0,0,0,0.45)',
                animation: 'bazaarBusyPulse 1.6s ease-in-out infinite',
              }}
            >
            <div
              style={{
                width: 96,
                height: 96,
                borderRadius: '50%',
                border: '6px solid rgba(255,255,255,0.85)',
                borderTopColor: '#4ECDC4',
                borderRightColor: '#ffb703',
                borderBottomColor: '#9b5de5',
                animation: 'bazaarBusySpin 1s linear infinite',
                boxSizing: 'border-box',
              }}
            />
            </div>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{busyLabel}</div>
          </div>
        </div>
      )}
      <style>{'@keyframes bazaarBusySpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } } @keyframes bazaarBusyPulse { 0% { transform: scale(1); opacity: 0.95; } 50% { transform: scale(1.06); opacity: 1; } 100% { transform: scale(1); opacity: 0.95; } }'}</style>
      <div className="profile-header-row">
        <button type="button" className="back-btn" onClick={handleBack}>← Back</button>
        <div style={{ flex: 1 }} />
        <button
          type="button"
          className="cancel-btn"
          onClick={handleExportVendorData}
          disabled={!eventId || loading || isExporting || salesforceBusy || attendeeExportBusy}
          style={{ marginRight: 8 }}
        >
          {isExporting ? 'Exporting...' : 'Download Vendor Data'}
        </button>
        <button
          type="button"
          className="cancel-btn"
          onClick={handleAttendeeDownload}
          disabled={!eventId || loading || salesforceBusy || isExporting || attendeeExportBusy}
          style={{ marginRight: 8 }}
        >
          {attendeeExportBusy ? 'Exporting...' : 'Download Attendee Data'}
        </button>
        <button type="button" className="cancel-btn" onClick={() => handleExitPage('/')}>Close</button>
      </div>

      <h2 className="profile-title">Admin: Bazaar Checkin</h2>
      {eventsError && <div className="profile-error" style={{ marginBottom: 8 }}>{eventsError}</div>}

      <div className="profile-grid" style={{ marginBottom: 12 }}>
        <div>
          <label className="profile-label">Event</label>
          <select
            className="profile-input"
            value={eventId || ''}
            onChange={(e) => setEventId(e.target.value)}
            disabled={eventsLoading || eventChoices.length === 0}
          >
            {eventsLoading && <option value="">Loading events…</option>}
            {!eventsLoading && eventChoices.length === 0 && <option value="">No events</option>}
            {!eventsLoading && eventChoices.length > 0 && (
              <>
                <option value="" disabled>Select an event…</option>
                {eventChoices.map(choice => (
                  <option key={choice.eventId} value={choice.eventId}>{choice.eventName}</option>
                ))}
              </>
            )}
          </select>
          {eventId && <div className="profile-hint">Event ID: {eventId}</div>}
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          <div className="profile-hint">Selecting a different event will reload the check-in data for that event.</div>
        </div>
      </div>

      <div style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <h4 style={{ margin: 0 }}>Filters</h4>
          <button
            type="button"
            className="cancel-btn"
            onClick={() => {
              setVendorTypeFilter('');
              setVendorNameFilter('');
              setContactNameFilter('');
              setCheckinDateFilter('');
              setBoothNumberFilter('');
              setConsentSignedFilter('all');
              setCheckedInOnly(false);
              setCheckedOutOnly(false);
            }}
            style={{ padding: '6px 12px', fontSize: '14px' }}
          >
            Reset Filters
          </button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '12px' }}>
          <div>
            <label className="profile-label">Vendor Type</label>
            <input className="profile-input" value={vendorTypeFilter} onChange={e => setVendorTypeFilter(e.target.value)} placeholder="e.g. Food" />
          </div>
          <div>
            <label className="profile-label">Vendor Name</label>
            <input className="profile-input" value={vendorNameFilter} onChange={e => setVendorNameFilter(e.target.value)} placeholder="e.g. Acme" />
          </div>
          <div>
            <label className="profile-label">Contact Name</label>
            <input className="profile-input" value={contactNameFilter} onChange={e => setContactNameFilter(e.target.value)} placeholder="e.g. Jane Doe" />
          </div>
          <div>
            <label className="profile-label">Consent Signed</label>
            <select
              className="profile-input"
              value={consentSignedFilter}
              onChange={(e) => setConsentSignedFilter(e.target.value)}
            >
              <option value="all">All</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 40, alignSelf: 'end' }}>
            <input
              type="checkbox"
              className="consent-sign-checkbox"
              checked={checkedInOnly}
              onChange={(e) => setCheckedInOnly(e.target.checked)}
            />
            <span>Checked In</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 40, alignSelf: 'end' }}>
            <input
              type="checkbox"
              className="consent-sign-checkbox"
              checked={checkedOutOnly}
              onChange={(e) => setCheckedOutOnly(e.target.checked)}
            />
            <span>Checked Out</span>
          </label>
          <div>
            <label className="profile-label">Chosen Check-in Date</label>
            <select
              className="profile-input"
              value={checkinDateFilter}
              onChange={(e) => setCheckinDateFilter(e.target.value)}
              disabled={!eventId}
            >
              <option value="">All dates</option>
              {selectedEventCheckinDateOptions.map((row) => (
                <option key={`${row.sequence}-${row.date}`} value={row.date}>
                  {row.date}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="profile-label">Booth #</label>
            <input
              className="profile-input"
              value={boothNumberFilter}
              onChange={(e) => setBoothNumberFilter(e.target.value)}
              placeholder="e.g. 101"
            />
          </div>
        </div>
      </div>

      {error && <div className="profile-error" style={{ marginBottom: 8 }}>{error}</div>}

      <div style={{ overflowX: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 12, flexWrap: 'wrap' }}>
          <div className="profile-hint" style={{ margin: 0 }}>
            {lastLoadedAt ? `Loaded: ${fmtDateTime(lastLoadedAt) || lastLoadedAt}` : 'Data not loaded yet'}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <div className="profile-hint" style={{ margin: 0 }}>
              Selected: {selectedCount}
            </div>
            <button
              type="button"
              className="apply-btn"
              onClick={handleMassSalesforceUpdate}
              disabled={!eventId || loading || salesforceBusy || selectedCount === 0}
            >
              {salesforceBusy ? 'Updating Salesforce…' : 'Update Salesforce'}
            </button>
            <button
              type="button"
              className="apply-btn"
              onClick={handleOpenSelectedEmailsModal}
              disabled={!eventId || loading || salesforceBusy || selectedCount === 0}
              title={selectedCount === 0 ? 'Select one or more rows to copy emails' : 'Copy selected vendor and primary contact emails'}
            >
              Copy Selected Email IDs
            </button>
            <button
              type="button"
              className="apply-btn"
              onClick={handleRefresh}
              disabled={!eventId || loading || salesforceBusy}
            >
              {loading ? 'Refreshing…' : 'Refresh Data'}
            </button>
          </div>
        </div>
        {salesforceMsg && (
          <div className={salesforceMsg.toLowerCase().includes('failed') || salesforceMsg.toLowerCase().includes('missing') ? 'profile-error' : 'profile-hint'} style={{ marginBottom: 8 }}>
            {salesforceMsg}
          </div>
        )}
        {!loading && (
          <div style={{ padding: 12, borderBottom: '2px solid #ccc', background: '#fafafa', marginBottom: 8 }}>
            <div style={{ fontWeight: 700 }}>
              Totals — Vendors: {summaryFiltered.vendors || 0} • Booths: {summaryFiltered.booths || 0} • Attendees: {summaryFiltered.attendees || 0} • Signed: {summaryFiltered.signed || 0} • Checked In: {summaryFiltered.checkedIn || 0} • Checked Out: {summaryFiltered.checkedOut || 0}
            </div>
            {dateCountSummary.length > 0 && (
              <div style={{ marginTop: 4, fontWeight: 600, fontSize: 13, color: '#374151' }}>
                By Check-in Date — {dateCountSummary.join(' • ')}
              </div>
            )}
          </div>
        )}
        {loading ? (
          <div>Loading…</div>
        ) : (
          <table className="profile-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'center', padding: '8px', width: 36 }}>
                  <input
                    type="checkbox"
                    className="consent-sign-checkbox"
                    checked={allFilteredSelected}
                    onChange={(e) => toggleSelectAllFiltered(e.target.checked)}
                    aria-label="Select all filtered vendors"
                  />
                </th>
                <th style={{ textAlign: 'left', padding: '8px' }}>
                  <span style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => { setSortBy('vendor'); setSortDir(sortBy === 'vendor' ? (sortDir === 'asc' ? 'desc' : 'asc') : 'asc'); }}>
                    Vendor {sortBy === 'vendor' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                  </span>
                </th>
                <th style={{ textAlign: 'left', padding: '8px' }}>
                  <span style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => { setSortBy('type'); setSortDir(sortBy === 'type' ? (sortDir === 'asc' ? 'desc' : 'asc') : 'asc'); }}>
                    Type {sortBy === 'type' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                  </span>
                </th>
                <th style={{ textAlign: 'left', padding: '8px' }}>
                  <span style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => { setSortBy('contact'); setSortDir(sortBy === 'contact' ? (sortDir === 'asc' ? 'desc' : 'asc') : 'asc'); }}>
                    Contact Name {sortBy === 'contact' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                  </span>
                </th>
                <th style={{ textAlign: 'left', padding: '8px' }}>
                  <span style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => { setSortBy('booths'); setSortDir(sortBy === 'booths' ? (sortDir === 'asc' ? 'desc' : 'asc') : 'asc'); }}>
                    # of Booths {sortBy === 'booths' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                  </span>
                </th>
                <th style={{ textAlign: 'left', padding: '8px' }}>
                  <span style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => { setSortBy('attendees'); setSortDir(sortBy === 'attendees' ? (sortDir === 'asc' ? 'desc' : 'asc') : 'asc'); }}>
                    # of Attendees {sortBy === 'attendees' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                  </span>
                </th>
                <th style={{ textAlign: 'left', padding: '8px' }}>
                  <span style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => { setSortBy('checkindate'); setSortDir(sortBy === 'checkindate' ? (sortDir === 'asc' ? 'desc' : 'asc') : 'asc'); }}>
                    Check-in Date {sortBy === 'checkindate' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                  </span>
                </th>
                <th style={{ textAlign: 'left', padding: '8px' }}>
                  <span style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => { setSortBy('signed'); setSortDir(sortBy === 'signed' ? (sortDir === 'asc' ? 'desc' : 'asc') : 'asc'); }}>
                    Signed {sortBy === 'signed' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                  </span>
                </th>
                <th style={{ textAlign: 'left', padding: '8px' }}>
                  <span style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => { setSortBy('checkin'); setSortDir(sortBy === 'checkin' ? (sortDir === 'asc' ? 'desc' : 'asc') : 'asc'); }}>
                    Checked In {sortBy === 'checkin' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                  </span>
                </th>
                <th style={{ textAlign: 'left', padding: '8px' }}>
                  <span style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => { setSortBy('checkout'); setSortDir(sortBy === 'checkout' ? (sortDir === 'asc' ? 'desc' : 'asc') : 'asc'); }}>
                    Checked Out {sortBy === 'checkout' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedFiltered.length === 0 ? (
                <tr>
                  <td colSpan={10} style={{ padding: 12, textAlign: 'center', color: '#666' }}>No vendors match the filters</td>
                </tr>
              ) : (
                sortedFiltered.map(row => (
                  <tr key={row.vendorId}>
                    <td style={{ padding: '8px', borderTop: '1px solid #eee', textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        className="consent-sign-checkbox"
                        checked={selectedVendorIds.has(String(row.vendorId || '').trim())}
                        onChange={() => toggleRowSelection(row.vendorId)}
                        aria-label={`Select ${row.vendorName || row.vendorId}`}
                      />
                    </td>
                    <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>
                      <span
                        style={{ color: '#1a6bc1', cursor: 'pointer', textDecoration: 'underline' }}
                        onClick={() => handleOpenVendorDetail(row.vendorId)}
                      >
                        {row.vendorName || row.vendorId}
                      </span>
                    </td>
                    <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>{row.vendorType || ''}</td>
                    <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>{row.contactName || ''}</td>
                    <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>{row.boothCount || 0}</td>
                    <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>{row.attendeeCount || 0}</td>
                    <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>{row.selectedCheckinDate || '—'}</td>
                    <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>{row.consentSigned === true ? 'Yes' : 'No'}</td>
                    <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>{fmtDateTime(row.checkinAt) || '-'}</td>
                    <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>{fmtDateTime(row.checkoutAt) || '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
      <EmailRecipientsModal
        open={showEmailModal}
        title="Selected Email IDs"
        subtitle="Vendor and primary contact emails for selected rows. Edit as needed, then copy."
        initialValue={emailModalText}
        onClose={() => setShowEmailModal(false)}
      />
    </div>
  );
};

export default BazaarCheckin;
